import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { format } from 'date-fns';

import {
  TimelineItem,
  TimelineSeparator,
  TimelineDot,
  TimelineConnector,
  TimelineContent,
  TimelineOppositeContent,
} from '@mui/lab';
import { Button } from '@mui/material';

import { useSelector } from 'react-redux';
import { selectCurrentLocation } from '../../store/map/map.selector';

import './episode-item.styles.scss';
import ItemName from './item-name.component';
import ZoneItemName from '../episode-item/item-name-zone.component';

export default function EpisodeItem({ episode, metric, countyNames }) {
  const [expandText, setExpandText] = useState(false);
  const loc = useSelector(selectCurrentLocation);

  const isMoney = metric.includes('damage');

  const sDate = format(episode.beginDate, 'MM/dd');
  const eDate = format(episode.endDate, 'MM/dd');
  const date = sDate === eDate ? sDate : `${sDate}-${eDate}`;

  const countySelected = loc.id.length === 5;
  const countyAndNotCount = countySelected && metric !== 'count';

  let title = '';
  if (metric === 'count') {
    title = 'Impacted Counties';
  } else {
    title = `Impact by ${countySelected ? 'Event ID' : 'County'}`;
  }

  let total = 0;
  let itemData = {};
  episode.events.forEach((event) => {
    const amount = event[metric];
    
    let name = null;
    if (countyAndNotCount) {
      name = event.event;
    } else if (typeof event.counties === 'string') {
      name = countyNames[loc.id];
    } else if (event.counties.length === 1) {
      name = countyNames[event.counties[0]];
    } else if (event.counties.length > 1) {
      name = event.affectedZoneName.slice(0,1) + event.affectedZoneName.slice(1).toLowerCase();
    }

    if (Object.keys(itemData).includes(name)) {
      itemData[name].amount += amount;
      itemData[name].events.push(event);
    } else {
      itemData[name] = {
        amount,
        events: [event],
      };
    }

    if (event.counties.length > 1) {
      itemData[name]['extra'] = event.counties.map(fip => countyNames[fip]);
    }

    total += amount;
  });

  let itemList = Object.keys(itemData)
    .sort()
    .reduce((itemNames, name) => {
      if (metric === 'count') {
        if (name) {
          if (itemData[name].extra) {
            itemNames.push(
              <ZoneItemName
                key={name}
                date={date}
                events={itemData[name].events}
                name={name}
                extra={itemData[name].extra}
              />
            );
          } else {
            itemNames.push(
              <ItemName
                key={name}
                date={date}
                events={itemData[name].events}
                name={name}
              />
            );
          }
        }
      } else {
        if (itemData[name].extra) {
          itemNames.push(
            <ZoneItemName
              key={name}
              {...itemData[name]}
              name={name}
              isMoney={isMoney}
              date={date}
              extra={itemData[name].extra}
            />
          );
        } else {
          itemNames.push(
            <ItemName
              key={name}
              {...itemData[name]}
              name={name}
              isMoney={isMoney}
              date={date}
            />
          );
        }
      }

      return itemNames;
    }, []);

  if (metric !== 'count' && itemList.length > 1) {
    itemList.push(
      <ItemName key='total' name='Total' amount={total} isMoney={isMoney} />
    );
  }

  return (
    <TimelineItem sx={{ minHeight: 'fit-content' }}>
      <TimelineContent
        sx={{
          maxWidth: '50px',
          overflowWrap: 'break-word',
          padding: 0,
          color: 'rgb(100,100,100)',
          lineHeight: 1.2,
        }}
      >
        {date}
      </TimelineContent>
      <TimelineSeparator>
        <TimelineDot sx={{ backgroundColor: '#1976D2' }} />
        <TimelineConnector />
      </TimelineSeparator>
      <TimelineOppositeContent
        sx={{ width: 'calc(100% - 74px)', padding: '6px 0px 6px 6px' }}
      >
        <div className='episode-item'>
          <div className='episode-item-spacer'>
            <div className='episode-item-upper'>
              <div className='eiu-title'>{title}</div>
              <div className='eiu-list'>
                <ul>
                  {itemList.map((item, i) => (
                    <li key={i}>{item}</li>
                  ))}
                </ul>
              </div>
            </div>
            <hr className='episode-item-divider' />
            <div
              className={`episode-item-episode${
                expandText ? '' : ' collapsed'
              }`}
            >
              {episode.narrative
                ? episode.narrative
                : 'No narrative is available for this episode.'}
            </div>
            <Button
              onClick={() => setExpandText(!expandText)}
              className='expand-btn'
            >
              {expandText ? 'Read Less' : 'Read More'}
            </Button>
          </div>
        </div>
      </TimelineOppositeContent>
    </TimelineItem>
  );
}

EpisodeItem.propTypes = {
  episode: PropTypes.object,
  metric: PropTypes.string,
  countyNames: PropTypes.object,
};
